if (!customElements.get('hoobro-shop-the-look')) {
  customElements.define('hoobro-shop-the-look', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      if (!this.querySelector('.hb-look__grid')) {
        this.connectTimer = setTimeout(() => {
          if (this.isConnected && this.querySelector('.hb-look__grid')) this.connectedCallback();
        }, 0);
        return;
      }
      this.scrollTarget = undefined;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.hotspots = Array.from(this.querySelectorAll('.hb-look__hotspot'));
      this.rail = this.querySelector('.hb-look__grid');
      this.scenes = Array.from(this.rail.querySelectorAll('.hb-look__scene'));
      this.carousel = this.classList.contains('hb-look--carousel') || window.matchMedia('(max-width:749px)').matches;
      if (this.querySelector('.hb-look__arrows')) {
        this.querySelector('.hb-look__arrows').hidden = !this.carousel;
        this.querySelectorAll('[data-look-direction]').forEach(button => {
          button.addEventListener('click', () => this.moveScene(Number(button.dataset.lookDirection)), { signal });
        });
        this.rail.addEventListener('keydown', event => {
          if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          this.moveScene(event.key === 'ArrowRight' ? 1 : -1);
        }, { signal });
        ['pointerdown', 'wheel'].forEach(type => this.rail.addEventListener(type, () => { this.scrollTarget = undefined; }, { signal, passive: true }));
        this.rail.addEventListener('scroll', () => {
          cancelAnimationFrame(this.carouselFrame);
          this.carouselFrame = requestAnimationFrame(() => this.updateCarousel());
        }, { signal, passive: true });
      }
      this.gallery = this.querySelector('.hb-look__gallery');
      this.gallery.addEventListener('pointerdown', event => {
        this.dataset.keyboardActive = 'false';
        if (event.pointerType !== 'touch') return;
        clearTimeout(this.touchTimer);
        this.gallery.dataset.touchActive = 'true';
        this.touchTimer = setTimeout(() => { this.gallery.dataset.touchActive = 'false'; }, 3000);
      }, { signal });
      this.addEventListener('focusin', event => { this.dataset.keyboardActive = String(event.target.matches(':focus-visible')); }, { signal });
      this.addEventListener('focusout', () => {
        queueMicrotask(() => { this.dataset.keyboardActive = String(this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')); });
      }, { signal });
      this.querySelectorAll('.hb-look__media img').forEach(image => {
        image.draggable = false;
        image.addEventListener('load', () => { this.updateHotspotPositions(); this.updateCarousel(); }, { signal });
      });
      this.updateHotspotPositions();
      this.updateCarousel();
      this.hotspots.forEach(hotspot => {
        const trigger = hotspot.querySelector('.hb-look__trigger');
        const panel = hotspot.querySelector('.hb-look__panel');
        hotspot.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.open(hotspot);
        }, { signal });
        hotspot.addEventListener('pointerleave', event => {
          if (event.pointerType === 'mouse') this.scheduleClose(hotspot);
        }, { signal });
        panel.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), { signal });
        trigger.addEventListener('pointerdown', event => {
          this.pointerType = event.pointerType;
          this.touchStart = event.pointerType === 'touch' ? { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false } : null;
        }, { signal });
        trigger.addEventListener('pointermove', event => {
          if (this.touchStart?.id === event.pointerId && Math.hypot(event.clientX - this.touchStart.x, event.clientY - this.touchStart.y) > 10) this.touchStart.moved = true;
        }, { signal });
        trigger.addEventListener('pointercancel', () => { this.touchStart = null; }, { signal });
        trigger.addEventListener('pointerup', event => {
          const start = this.touchStart;
          this.touchStart = null;
          if (event.pointerType !== 'touch' || start?.id !== event.pointerId || start.moved) return;
          const releasedOn = document.elementFromPoint(event.clientX, event.clientY);
          if (!trigger.contains(releasedOn)) return;
          this.touchClickUntil = Date.now() + 1000;
          if (this.active === hotspot) this.close();
          else this.open(hotspot);
        }, { signal });
        trigger.addEventListener('focus', () => {
          if (this.suppressedFocus !== hotspot && this.pointerType !== 'touch' && trigger.matches(':focus-visible')) this.open(hotspot);
        }, { signal });
        trigger.addEventListener('click', event => {
          if (event.detail > 0 && Date.now() < (this.touchClickUntil || 0) && this.pointerType === 'touch') return;
          if (event.detail === 0) {
            this.open(hotspot);
            panel.querySelector('a[href]')?.focus();
          } else if (this.active === hotspot) this.close();
          else this.open(hotspot);
        }, { signal });
        trigger.addEventListener('keydown', event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open(hotspot);
            panel.querySelector('a[href]')?.focus();
          }
        }, { signal });
        hotspot.addEventListener('focusout', event => {
          if (!hotspot.contains(event.relatedTarget) && this.active === hotspot) this.close();
        }, { signal });
        panel.querySelector('.hb-look__close').addEventListener('click', () => this.close(true), { signal });
      });
      document.addEventListener('pointerdown', event => {
        if (this.active && !this.active.contains(event.target)) this.close();
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Tab') this.pointerType = 'keyboard';
        if (event.key === 'Escape' && this.active) {
          event.preventDefault();
          this.close(this.active.contains(document.activeElement));
        }
      }, { signal });
      window.addEventListener('resize', () => { this.updateHotspotPositions(); this.updateCarousel(); this.reposition(); }, { signal, passive: true });
      window.addEventListener('scroll', event => {
        if (!this.active) return;
        const panel = this.active.querySelector('.hb-look__panel');
        if (event.target instanceof Node && panel.contains(event.target)) return;
        if (this.editorSelected) this.reposition();
        else this.close();
      }, { signal, capture: true, passive: true });
      this.addEventListener('shopify:block:select', event => {
        const hotspot = event.target.closest('.hb-look__hotspot');
        if (!hotspot || !this.contains(hotspot)) return;
        this.editorSelected = hotspot;
        hotspot.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this.open(hotspot);
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = null;
        this.close();
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => { this.updateHotspotPositions(); this.updateCarousel(); this.reposition(); });
      this.resizeObserver.observe(this);
      this.hotspots.forEach(hotspot => this.resizeObserver.observe(hotspot.querySelector('.hb-look__panel')));
    }

    open(hotspot) {
      if (this.carousel && hotspot.dataset.inView === 'false' && !this.editorSelected) return;
      clearTimeout(this.closeTimer);
      if (this.active && this.active !== hotspot) this.close();
      this.active = hotspot;
      hotspot.dataset.open = 'true';
      hotspot.closest('.hb-look__scene').dataset.popupOpen = 'true';
      hotspot.querySelector('.hb-look__trigger').setAttribute('aria-expanded', 'true');
      const panel = hotspot.querySelector('.hb-look__panel');
      panel.hidden = false;
      panel.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      this.positionPanel();
    }

    scheduleClose(hotspot) {
      clearTimeout(this.closeTimer);
      if (this.editorSelected === hotspot || hotspot.contains(document.activeElement)) return;
      this.closeTimer = setTimeout(() => {
        if (this.active === hotspot && !hotspot.matches(':hover') && !hotspot.contains(document.activeElement)) this.close();
      }, 180);
    }

    close(restoreFocus = false) {
      clearTimeout(this.closeTimer);
      if (!this.active) return;
      const hotspot = this.active;
      this.active = null;
      hotspot.dataset.open = 'false';
      hotspot.closest('.hb-look__scene').dataset.popupOpen = 'false';
      hotspot.querySelector('.hb-look__panel').hidden = true;
      const trigger = hotspot.querySelector('.hb-look__trigger');
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) {
        this.suppressedFocus = hotspot;
        trigger.focus({ preventScroll: true });
        queueMicrotask(() => { this.suppressedFocus = null; });
      }
    }

    updateHotspotPositions() {
      const mobile = window.matchMedia('(max-width:749px)').matches;
      this.scenes.forEach(scene => {
        const image = scene.querySelector('.hb-look__media img');
        if (!image?.naturalWidth || !scene.clientWidth || !scene.clientHeight) return;
        const scale = Math.max(scene.clientWidth / image.naturalWidth, scene.clientHeight / image.naturalHeight);
        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;
        const focal = getComputedStyle(image).objectPosition.split(' ');
        const focalX = focal[0]?.endsWith('%') ? parseFloat(focal[0]) / 100 : 0.5;
        const focalY = focal[1]?.endsWith('%') ? parseFloat(focal[1]) / 100 : 0.5;
        scene.querySelectorAll('.hb-look__hotspot').forEach(hotspot => {
          const style = getComputedStyle(hotspot);
          const x = parseFloat(style.getPropertyValue(mobile ? '--hb-look-mobile-x' : '--hb-look-x')) / 100;
          const y = parseFloat(style.getPropertyValue(mobile ? '--hb-look-mobile-y' : '--hb-look-y')) / 100;
          hotspot.style.setProperty('--hb-look-rendered-x', ((width * x + (scene.clientWidth - width) * focalX) / scene.clientWidth * 100) + '%');
          hotspot.style.setProperty('--hb-look-rendered-y', ((height * y + (scene.clientHeight - height) * focalY) / scene.clientHeight * 100) + '%');
        });
      });
    }

    moveScene(direction) {
      if (!this.carousel) return;
      const maximum = this.rail.scrollWidth - this.rail.clientWidth;
      const stops = [...new Set(this.scenes.map(scene => Math.min(maximum, scene.offsetLeft)))];
      const current = this.scrollTarget ?? this.rail.scrollLeft;
      const target = direction > 0 ? stops.find(stop => stop > current + 2) : stops.reverse().find(stop => stop < current - 2);
      if (target === undefined) return;
      this.close();
      this.scrollTarget = target;
      this.rail.scrollTo({ left: target, behavior: window.matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth' });
      this.updateCarousel();
    }

    updateCarousel() {
      this.carousel = this.classList.contains('hb-look--carousel') || window.matchMedia('(max-width:749px)').matches;
      this.querySelector('.hb-look__arrows').hidden = !this.carousel;
      if (!this.carousel) {
        this.hotspots.forEach(hotspot => {
          hotspot.dataset.inView = 'true';
          hotspot.querySelector('.hb-look__trigger').tabIndex = 0;
        });
        return;
      }
      const bounds = this.rail.getBoundingClientRect();
      const maximum = this.rail.scrollWidth - this.rail.clientWidth;
      if (this.scrollTarget !== undefined && Math.abs(this.rail.scrollLeft - this.scrollTarget) < 2) this.scrollTarget = undefined;
      const current = this.scrollTarget ?? this.rail.scrollLeft;
      this.querySelector('[data-look-direction="-1"]').disabled = current < 2;
      this.querySelector('[data-look-direction="1"]').disabled = current > maximum - 2;
      this.hotspots.forEach(hotspot => {
        const trigger = hotspot.querySelector('.hb-look__trigger');
        const point = trigger.getBoundingClientRect();
        const center = point.left + point.width / 2;
        const visible = center >= bounds.left && center <= bounds.right;
        hotspot.dataset.inView = String(visible);
        trigger.tabIndex = visible ? 0 : -1;
      });
    }

    reposition() {
      cancelAnimationFrame(this.positionFrame);
      if (this.active) this.positionFrame = requestAnimationFrame(() => this.positionPanel());
    }

    positionPanel() {
      if (!this.active) return;
      const trigger = this.active.querySelector('.hb-look__trigger').getBoundingClientRect();
      const panel = this.active.querySelector('.hb-look__panel');
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;
      const margin = 12;
      const centerX = trigger.left + trigger.width / 2;
      const centerY = trigger.top + trigger.height / 2;
      let direction = this.active.dataset.direction || 'auto';
      if (direction === 'auto') direction = window.matchMedia('(max-width:749px)').matches ? 'below' : 'right';
      let left;
      let top;
      if (direction === 'right' || direction === 'left') {
        const right = trigger.right + gap;
        const leftSide = trigger.left - width - gap;
        if (direction === 'right' && right + width > viewportWidth - margin && leftSide >= margin) direction = 'left';
        else if (direction === 'left' && leftSide < margin && right + width <= viewportWidth - margin) direction = 'right';
        left = direction === 'right' ? right : leftSide;
        top = centerY - height / 2;
      } else {
        const below = trigger.bottom + gap;
        const above = trigger.top - height - gap;
        if (direction === 'below' && below + height > viewportHeight - margin && above >= margin) direction = 'above';
        else if (direction === 'above' && above < margin && below + height <= viewportHeight - margin) direction = 'below';
        left = centerX - width / 2;
        top = direction === 'below' ? below : above;
      }
      panel.style.left = Math.max(margin, Math.min(left, viewportWidth - width - margin)) + 'px';
      panel.style.top = Math.max(margin, Math.min(top, viewportHeight - height - margin)) + 'px';
      panel.dataset.placement = direction;
    }

    disconnectedCallback() {
      clearTimeout(this.connectTimer);
      clearTimeout(this.touchTimer);
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.positionFrame);
      cancelAnimationFrame(this.carouselFrame);
      this.close();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
